import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Rocket, PhoneCall, Cpu } from 'lucide-react';
import { PricingTierCard, type PricingTier } from './ui/pricing-tier-card';

const tiers: PricingTier[] = [
  {
    name: 'Start',
    forWho: 'Dla firm, które mają już ruch (polecenia, organiczny) i chcą zamieniać go w umówione spotkania.',
    price: '5 000 zł',
    period: '/ miesiąc',
    Icon: Rocket,
    features: [
      'Strona / landing zoptymalizowany pod konwersję',
      'Chatbot AI na stronie, kwalifikuje klienta 24/7',
      'Kampanie Facebook i Instagram Ads (prowadzenie + kreacje)',
      'Kwalifikacja klienta i booking prosto do kalendarza',
      'Dashboard ze statystykami sprzedaży w czasie rzeczywistym',
      'Pełne wsparcie po stronie DAIEN',
    ],
    ctaText: 'Umów rozmowę',
    ctaHref: '#kontakt',
  },
  {
    name: 'Wzrost',
    forWho: 'Dla firm, które tracą leady przez nieodebrane telefony i zbyt wolny kontakt.',
    price: '7 000 zł',
    period: '/ miesiąc',
    highlighted: true,
    badgeText: 'Najpopularniejszy',
    Icon: PhoneCall,
    features: [
      'Wszystko z pakietu Start',
      'Voicebot speed-to-lead: odbiera i oddzwania w mniej niż minutę, 24/7',
      'Automatyczne oddzwanianie do każdego nowego leada',
      'Reaktywacja bazy: kampanie SMS do starych i zimnych kontaktów',
      'Rozbudowane statystyki sprzedaży: koszt leada, koszt spotkania, ROI',
    ],
    footnote: 'Kontakt w mniej niż minutę to nawet 3x wyższa konwersja.',
    ctaText: 'Umów rozmowę',
    ctaHref: '#kontakt',
  },
  {
    name: 'System na Zamówienie',
    forWho: 'Dla firm, które chcą AI w całej organizacji, nie tylko w marketingu.',
    price: 'od 25 000 zł',
    period: 'wdrożenie',
    priceNote: '+ 4 000 zł / mc (utrzymanie, optymalizacja, reklamy)',
    Icon: Cpu,
    features: [
      'Audyt biznesowy: gdzie tracisz czas i pieniądze, gdzie AI da największy zwrot',
      'Custom software pod Twoje procesy (automatyzacje szyte na miarę)',
      'Wszystko z pakietu Wzrost',
      'Szkolenia zespołu z Claude Code i pracy z AI na co dzień',
      'Dedykowane wsparcie z priorytetem',
    ],
    ctaText: 'Porozmawiajmy o wdrożeniu',
    ctaHref: '#kontakt',
  },
];

/**
 * PricingSection - sekcja cennika na stronie glownej (#cennik).
 * Model good-better-best z kotwica: pakiet "System na Zamowienie" (od 25 000 zl)
 * urealnia pakiety miesieczne 5k i 7k. Wzorzec layoutu i animacji z SolutionsBenefits.
 */
export default function PricingSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section id="cennik" aria-labelledby="cennik-heading" className="py-16 md:py-24 bg-bg">
      <div className="max-w-[1200px] mx-auto px-6 md:px-10 lg:px-16">

        {/* Naglowek */}
        <motion.div
          className="mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 1, ease: 'easeOut' }}
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-8 h-px bg-stroke"></div>
            <span className="text-xs text-muted uppercase tracking-[0.3em]">Cennik</span>
          </div>
          <h2 id="cennik-heading" className="text-4xl md:text-5xl lg:text-6xl tracking-tight font-light text-text-primary mb-4">
            Wybierz pakiet <span className="font-display italic">dopasowany do Ciebie</span>
          </h2>
          <p className="max-w-2xl text-base md:text-lg text-muted">
            Jasne kwoty, bez ukrytych kosztów. Zaczynasz od gotowego systemu, a kiedy chcesz AI w całej firmie, budujemy je pod Ciebie.
          </p>
        </motion.div>

        {/* Siatka pakietow */}
        <div ref={ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8 items-stretch">
          {tiers.map((tier, idx) => (
            <motion.div
              key={tier.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
              transition={{ duration: 0.6, delay: 0.1 * idx, ease: 'easeOut' }}
              className="h-full"
            >
              <PricingTierCard {...tier} />
            </motion.div>
          ))}
        </div>

        {/* Pasek cross-tier */}
        <p className="mt-12 text-center text-sm text-muted">
          Budżet na reklamy płatny osobno, po stronie klienta. Bez opłaty wdrożeniowej na pakietach miesięcznych. Niestandardowe rozwiązania wyceniamy indywidualnie.
        </p>

      </div>
    </section>
  );
}
